import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  useMediaQuery,
} from '@mui/material';
import { GROUPBY } from '../../../constants/Reports';
import SelectRebuild from '../../../components/Inputs/SelectRebuild';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
  selectCSVWoburnOvertimeReport,
  selectEmployeeData,
  selectWoburnOvertimeReport,
} from '../../../store/ReportsDetailsSlice/selectors';
import { ExportCsvButton } from '../../../components/Buttons/ExportCsvButton';
import DataNotFound from '../../../components/Reports/DataNotFound';
import ReportsHeader from '../../../components/Reports/ReportsHeader';
import { FilterPanel } from '../../../components/Reports/FilterPanel';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter';
import {
  getCSVWoburnOvertimeReportData,
  getEmployeeList,
  getWoburnOvertimeReportData,
  setCSVWoburnOvertimeReportData,
} from '../../../store/ReportsDetailsSlice/actions';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';
import moment from 'moment';
import { saveCsvToFile } from '../../../utils/saveCsvToFile';
import useColumns from './hooks/useColumns';
import { useOvertimeRows } from './hooks/useOvertimeRows';
import { useOogRows } from './hooks/useOogRows';
import { useTownRows } from './hooks/useTownRows';
import { usePrivateRows } from './hooks/usePrivateRows';
import { TableForReusableGrid } from './components/TableForReusableGrid';
import { TableForArrayTable } from './components/TableForArrayTable';
import { useStipendsSORows } from './hooks/useStipendsSORows';

const defaultFormValues = {
  emp_code: { id: 'ALL', name: 'All' },
  groupby: { id: 'Emp', name: 'Employee' },
};

const WoburnOvertimeReport = () => {
  const dispatch = useDispatch();
  const { woburnOvertimeReportData, isWoburnOvertimeReportLoading } =
    useSelector(selectWoburnOvertimeReport);
  const { woburnOvertimeCSVReportData, isLoadingCSVWoburnOvertimeReport } =
    useSelector(selectCSVWoburnOvertimeReport);
  const { employeeList, loadingEmployeeList } = useSelector(selectEmployeeData);
  const isTabletVerticalWidth = useMediaQuery('(max-width:1024px)');
  const isTabletWidth = useMediaQuery('(max-width:899px)');

  const { register, setValue, watch } = useForm({
    defaultValues: defaultFormValues,
  });
  const { emp_code: employees, groupby } = watch();

  const { fromDate, toDate, render } = useDateRangeFilter();

  const handleDownloadReport = () => {
    const query = { ...param, csv: 1 };
    dispatch(getCSVWoburnOvertimeReportData(query));
  };

  const handleFindClick = () => dispatch(getWoburnOvertimeReportData(param));

  const param = useMemo(() => {
    return {
      start_dt: moment(fromDate).format('YYYY-MM-DD') + ' 00:00:00',
      end_dt: moment(toDate).format('YYYY-MM-DD') + ' 23:59:59',
      sel_order: groupby.id,
      sel_emp: employees ? employees.id : 'ALL',
    };
  }, [fromDate, toDate, groupby, employees]);

  useEffect(() => {
    dispatch(getWoburnOvertimeReportData(param));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupby]);

  useEffect(() => {
    dispatch(getEmployeeList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (woburnOvertimeCSVReportData.data) {
      saveCsvToFile(
        woburnOvertimeCSVReportData.data,
        woburnOvertimeCSVReportData.filename
      );
      dispatch(setCSVWoburnOvertimeReportData({ data: null, filename: '' }));
    }
  }, [woburnOvertimeCSVReportData, dispatch]);

  const rawFilterConfig = [
    {
      key: 'group_by',
      label: 'Group By',
      show: GROUPBY.length > 0,
      renderComponent: () => (
        <SelectRebuild
          options={GROUPBY.map((item) => ({
            id: item.value,
            name: item.title,
          }))}
          name="groupby"
          label="Group By"
          multiple={false}
          loading={false}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'emp_code',
      label: 'Employee',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          enableAllOption
          options={employeeList}
          name="emp_code"
          label="Employee"
          multiple={false}
          allOption={{ id: 'ALL', name: 'All' }}
          loading={loadingEmployeeList}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'find_button',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <Button
            variant="contained"
            onClick={handleFindClick}
            sx={{ whiteSpace: 'nowrap', height: '32px' }}
          >
            Find
          </Button>
        </Grid>
      ),
    },
    {
      key: 'export',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            gap: 2,
            justifyContent: isTabletVerticalWidth ? 'start' : 'end',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <ExportCsvButton
            onClick={handleDownloadReport}
            disabled={isLoadingCSVWoburnOvertimeReport}
            text={'Export CSV'}
          />
        </Grid>
      ),
    },
  ];
  const filterConfig = rawFilterConfig.filter((f) => f.show !== false);

  const overtimeData = woburnOvertimeReportData?.overtime?.data || {};
  const oogData = woburnOvertimeReportData?.oog?.data || {};
  const townData = woburnOvertimeReportData?.town?.data || {};
  const privateData = woburnOvertimeReportData?.private?.data || {};
  const stipendsSOData = woburnOvertimeReportData?.stipends_so?.data || {};

  const overtimeColumns = useColumns('overtime', groupby.id);
  const overtimeRows = useOvertimeRows(
    overtimeData,
    overtimeColumns,
    groupby.id
  );

  const oogColumns = useColumns('oog', groupby.id);
  const oogRows = useOogRows(oogData, oogColumns);

  const townColumns = useColumns('town', groupby.id);
  const townRows = useTownRows(townData, townColumns, groupby.id);

  const privateColumns = useColumns('private', groupby.id);
  const privateRows = usePrivateRows(privateData, privateColumns, groupby.id);

  const stipendsSOColumns = useColumns('stipendsSO', groupby.id);
  const useStipedsSORows = useStipendsSORows(
    stipendsSOData,
    stipendsSOColumns,
    groupby.id
  );

  const buildArrayTables = (section) => {
    const headers = section?.headers || [];
    const data = section?.data;
    if (!data) return [];

    if (Array.isArray(data)) {
      return data.length ? [{ name: null, headers, rows: data }] : [];
    }

    return Object.entries(data || {}).map(([name, rows]) => ({
      name,
      headers,
      rows: rows || [],
    }));
  };

  const stipendsOSTables = buildArrayTables(
    woburnOvertimeReportData?.stipends_os
  );
  const stipendsSOTables = buildArrayTables(
    woburnOvertimeReportData?.stipends_so
  );

  const hasAnyData =
    overtimeRows.length > 0 ||
    oogRows.length > 0 ||
    townRows.length > 0 ||
    privateRows.length > 0 ||
    [stipendsOSTables, stipendsSOTables].some((group) =>
      group.some((t) => (t.rows || []).length > 0)
    );
  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <ReportsHeader title="Woburn Overtime Report Details" />

      <Box className="mt-4 mb-4" sx={{ width: '100%' }}>
        <Grid container sx={{ width: '100%', mb: '24px' }}>
          <FilterPanel
            filters={filterConfig}
            columns={isTabletVerticalWidth ? 1 : 4}
          />
        </Grid>

        <Grid container sx={{ width: '100%' }}>
          <Grid
            item
            sx={{
              display: 'flex',
              gap: 2,
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Grid
              item
              sx={{
                display: 'flex',
                flexDirection: isTabletWidth ? 'column' : 'row',
                gap: 2,
                width: '100%',
                mt: 'auto',
              }}
            >
              {render}
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {isWoburnOvertimeReportLoading ? (
        <Box
          sx={{ minHeight: '30px' }}
          className="w-10 h-10 d-flex justify-content-center align-items-center"
        >
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 4, flexDirection: 'column' }}>
          {!hasAnyData ? (
            <DataNotFound />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <TableForReusableGrid
                title="Overtime"
                arr={overtimeRows}
                columns={overtimeColumns}
              />

              <TableForReusableGrid
                title="Out Of Grade"
                arr={oogRows}
                columns={oogColumns}
              />

              <TableForReusableGrid
                title="Private"
                arr={privateRows}
                columns={privateColumns}
              />

              <TableForArrayTable
                arr={stipendsOSTables}
                title="Stipends (OS)"
              />

              <TableForReusableGrid
                title="Stipends (SO)"
                arr={useStipedsSORows}
                columns={stipendsSOColumns}
              />

              <TableForReusableGrid
                title="Town"
                arr={townRows}
                columns={townColumns}
              />
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default WoburnOvertimeReport;
